import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { profileService } from '../services/profileService';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import type { ProfileData } from '../models/v2/profile';

// Define the shape of the Context
interface ProfileContextType {
    data: ProfileData | null;
    isLoading: boolean;
    error: string | null;
    refreshProfile: () => Promise<void>;
    updateLocalState: (updater: (prev: ProfileData | null) => ProfileData | null) => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated } = useAuth();
    const [data, setData] = useState<ProfileData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = useCallback(async (silent = false) => {
        if (!isAuthenticated) return;

        if (!silent) setIsLoading(true);
        setError(null);
        try {
            const profileData = await profileService.getProfile();
            setData(profileData);
        } catch (err: any) {
            console.error("Failed to fetch profile:", err);
            // Only set global blocking error if it's an initial load
            if (!silent) setError(err.message || 'Failed to load profile');
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, [isAuthenticated]);

    // Initial fetch on mount or auth change
    useEffect(() => {
        if (isAuthenticated) {
            fetchProfile(false);
        } else {
            setData(null);
        }
    }, [isAuthenticated, fetchProfile]);

    const refreshProfile = async () => {
        await fetchProfile(true);
    };

    const updateLocalState = useCallback((updater: (prev: ProfileData | null) => ProfileData | null) => {
        setData(prev => updater(prev));
    }, []);

    return (
        <ProfileContext.Provider value={{ data, isLoading, error, refreshProfile, updateLocalState }}>
            {children}
        </ProfileContext.Provider>
    );
}

export function useProfile() {
    const context = useContext(ProfileContext);
    if (context === undefined) {
        throw new Error('useProfile must be used within a ProfileProvider');
    }
    return context;
}

// Reusable Mutation Hook
interface MutationOptions<T> {
    mutationFn: (data: T) => Promise<any>;
    onSuccess?: (result: any) => void;
    onError?: (error: any) => void;
    successMessage?: string;
}

export function useProfileMutation<T>() {
    const { refreshProfile } = useProfile();
    const [isMutating, setIsMutating] = useState(false);

    const mutate = async (data: T, options: MutationOptions<T>) => {
        setIsMutating(true);
        try {
            const result = await options.mutationFn(data);

            // 1. Success Notification
            if (options.successMessage) {
                toast.success(options.successMessage);
            }

            // 2. Refresh Global Store
            await refreshProfile();

            // 3. Callback
            if (options.onSuccess) {
                options.onSuccess(result);
            }
        } catch (error: any) {
            console.error("Mutation failed:", error);
            toast.error(error.message || 'Operation failed');
            if (options.onError) {
                options.onError(error);
            }
        } finally {
            setIsMutating(false);
        }
    };

    return { mutate, isMutating };
}
